import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import style from './primitives.css'

/**
 * A page link in a composed [Pagination](/components/pagination/). A layout
 * wrapper around your own interactive element — provide an `<a>` (e.g. a
 * framework `<Link>` for crawlable, SSR-friendly links) or a `nord-button`. Set
 * `current` on the link for the active page; it mirrors `aria-current="page"`
 * onto the interactive child so assistive tech announces it.
 *
 * @status draft
 * @category navigation
 * @lightDom true
 *
 * @slot - Default slot for the page link or button.
 */
@customElement('nord-pagination-link')
export default class PaginationLink extends DraftComponentMixin(LitElement) {
  /** Marks this as the current page, mirroring `aria-current="page"` onto the child. */
  @property({ reflect: true, type: Boolean }) current = false

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)
    this.syncCurrent()
  }

  protected createRenderRoot() {
    return this
  }

  // No render() — the consumer's link/button is preserved as the element's own
  // content; we only reflect `current` onto it.
  protected updated() {
    this.syncCurrent()
  }

  // Whether we set `aria-current` on the child, so we only ever clear our own —
  // a consumer who sets `aria-current` themselves (without `current`) keeps it.
  private ownsAriaCurrent = false

  private syncCurrent() {
    const link = this.querySelector('a, button, nord-button')
    if (!link) {
      return
    }
    if (this.current) {
      link.setAttribute('aria-current', 'page')
      this.ownsAriaCurrent = true
    }
    else if (this.ownsAriaCurrent) {
      link.removeAttribute('aria-current')
      this.ownsAriaCurrent = false
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-pagination-link': PaginationLink
  }
}
